import React, { useState } from 'react';
import { DatePicker, Button, Popover } from 'antd';
import { CloseOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

export interface DateRangeValue {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

export interface SubmittedDateRangeQuery {
  submittedOnFrom?: string;
  submittedOnTo?: string;
}

/**
 * Canonical date-filter contract shared by Travel Home, My Claims,
 * Approvals and Reports. Date-only values let the API apply the business
 * timezone consistently instead of depending on the browser timezone.
 */
export const getSubmittedDateRangeQuery = (
  range: DateRangeValue,
): SubmittedDateRangeQuery =>
  range.startDate && range.endDate
    ? {
        submittedOnFrom: range.startDate.format('YYYY-MM-DD'),
        submittedOnTo: range.endDate.format('YYYY-MM-DD'),
      }
    : {};

interface DateRangeQuickFilterProps {
  value?: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
}

export const getCurrentFinancialYearRange = (): DateRangeValue => {
  const now = dayjs();
  const startYear = now.month() >= 3 ? now.year() : now.year() - 1;
  return {
    startDate: dayjs(`${startYear}-04-01`).startOf('day'),
    endDate: dayjs(`${startYear + 1}-03-31`).endOf('day'),
  };
};

export const getStoredOrCurrentFinancialYearRange = (
  storageKey: string,
): DateRangeValue => {
  const savedRange = sessionStorage.getItem(storageKey);
  if (!savedRange) return getCurrentFinancialYearRange();

  try {
    const parsed = JSON.parse(savedRange) as {
      startDate?: string;
      endDate?: string;
    };
    const startDate = parsed.startDate ? dayjs(parsed.startDate) : null;
    const endDate = parsed.endDate ? dayjs(parsed.endDate) : null;

    if (startDate === null && endDate === null) {
      return { startDate: null, endDate: null };
    }

    if (startDate?.isValid() && endDate?.isValid()) {
      return { startDate, endDate };
    }
  } catch {
    sessionStorage.removeItem(storageKey);
    return getCurrentFinancialYearRange();
  }

  sessionStorage.removeItem(storageKey);
  return getCurrentFinancialYearRange();
};

export const getDateRangeFromSearchParams = (
  searchParams: URLSearchParams,
  fallback: DateRangeValue,
): DateRangeValue => {
  if (searchParams.get('dateRange') === 'all') {
    return { startDate: null, endDate: null };
  }

  const startDateValue = searchParams.get('submittedOnFrom');
  const endDateValue = searchParams.get('submittedOnTo');
  if (!startDateValue || !endDateValue) return fallback;

  const startDate = dayjs(startDateValue);
  const endDate = dayjs(endDateValue);
  const isStrictDateOnly = (value: string, parsed: Dayjs) =>
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    parsed.isValid() &&
    parsed.format('YYYY-MM-DD') === value;
  return isStrictDateOnly(startDateValue, startDate) &&
    isStrictDateOnly(endDateValue, endDate) &&
    !startDate.isAfter(endDate, 'day')
    ? { startDate, endDate }
    : fallback;
};

export const getPreviousBusinessWeekRange = (): DateRangeValue => {
  const today = dayjs();
  const daysSinceMonday = (today.day() + 6) % 7;
  const currentMonday = today.subtract(daysSinceMonday, 'day').startOf('day');
  const previousMonday = currentMonday.subtract(1, 'week');
  return {
    startDate: previousMonday,
    endDate: previousMonday.add(6, 'day').endOf('day'),
  };
};

const quickOptions = [
  {
    label: 'Current FY',
    getRange: getCurrentFinancialYearRange,
  },
  {
    label: 'Today',
    getRange: () => ({
      startDate: dayjs().startOf('day'),
      endDate: dayjs().endOf('day'),
    }),
  },
  {
    label: 'Yesterday',
    getRange: () => ({
      startDate: dayjs().subtract(1, 'day').startOf('day'),
      endDate: dayjs().subtract(1, 'day').endOf('day'),
    }),
  },
  {
    label: 'Last Week',
    getRange: getPreviousBusinessWeekRange,
  },
  {
    label: 'This Month',
    getRange: () => ({
      startDate: dayjs().startOf('month'),
      endDate: dayjs().endOf('month'),
    }),
  },
  {
    label: 'Last Month',
    getRange: () => ({
      startDate: dayjs().subtract(1, 'month').startOf('month'),
      endDate: dayjs().subtract(1, 'month').endOf('month'),
    }),
  },
  {
    label: 'Last 3 Months',
    getRange: () => ({
      startDate: dayjs().subtract(2, 'month').startOf('month'),
      endDate: dayjs().endOf('month'),
    }),
  },
  {
    label: 'All Time',
    getRange: (): DateRangeValue => ({ startDate: null, endDate: null }),
  },
];

const rangesMatch = (left: DateRangeValue, right: DateRangeValue) => {
  if (!left.startDate && !left.endDate && !right.startDate && !right.endDate) {
    return true;
  }
  return Boolean(
    left.startDate &&
      left.endDate &&
      right.startDate &&
      right.endDate &&
      left.startDate.isSame(right.startDate, 'day') &&
      left.endDate.isSame(right.endDate, 'day'),
  );
};

export const getDateRangeDisplayLabel = (range: DateRangeValue) => {
  if (!range.startDate || !range.endDate) return 'All Time';
  const preset = quickOptions.find(option =>
    rangesMatch(range, option.getRange()),
  );
  if (preset?.label === 'Current FY') {
    return `Current FY · ${range.startDate.format('YYYY')}–${range.endDate.format('YY')}`;
  }
  if (preset) return preset.label;
  return `${range.startDate.format('DD MMM YYYY')} – ${range.endDate.format('DD MMM YYYY')}`;
};

const DateRangeQuickFilter: React.FC<DateRangeQuickFilterProps> = ({
  value,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [dates, setDates] = useState<DateRangeValue>(
    value || { startDate: null, endDate: null },
  );
  const [tempDates, setTempDates] = useState<DateRangeValue>(
    value || { startDate: null, endDate: null },
  );

  React.useEffect(() => {
    if (open) setTempDates(value || { startDate: null, endDate: null });
  }, [open, value]);

  React.useEffect(() => {
    setDates(value || { startDate: null, endDate: null });
  }, [value]);

  const handleRangeChange = (range: [Dayjs | null, Dayjs | null] | null) => {
    setTempDates(
      range?.[0] && range[1]
        ? { startDate: range[0], endDate: range[1] }
        : { startDate: null, endDate: null },
    );
  };
  const handleQuickSelect = (getRange: () => DateRangeValue) =>
    setTempDates(getRange());
  const handleCancel = () => {
    setOpen(false);
    setTempDates(value || { startDate: null, endDate: null });
  };
  const handleSubmit = () => {
    setOpen(false);
    setDates(tempDates);
    onChange(tempDates);
  };
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDates({ startDate: null, endDate: null });
    setTempDates({ startDate: null, endDate: null });
    onChange({ startDate: null, endDate: null });
  };
  const displayText = getDateRangeDisplayLabel(dates);

  const content = (
    <div style={{ width: 'min(28rem, calc(100vw - 2rem))' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
        }}
      >
        {quickOptions.map(opt => {
          const selected = rangesMatch(tempDates, opt.getRange());
          return (
            <Button
              key={opt.label}
              size="small"
              style={{
                background: selected ? '#4649EC' : '#f0f1f3',
                color: selected ? '#fff' : '#4649EC',
                border: selected
                  ? '1px solid #4649EC'
                  : '1px solid transparent',
                fontWeight: 500,
                textAlign: 'center',
                transition: 'background 0.2s, color 0.2s',
              }}
              onClick={() => handleQuickSelect(opt.getRange)}
            >
              {opt.label}
            </Button>
          );
        })}
      </div>
      <DatePicker.RangePicker
        value={
          tempDates.startDate && tempDates.endDate
            ? [tempDates.startDate, tempDates.endDate]
            : null
        }
        onChange={handleRangeChange}
        allowClear
        style={{ width: '100%', marginBottom: 16, marginTop: 4 }}
        format="DD.MM.YYYY"
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button
          size="middle"
          style={{
            background: '#f0f1f3',
            color: '#4649EC',
            border: 'none',
            fontWeight: 500,
            minWidth: 80,
          }}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          size="middle"
          type="primary"
          style={{ fontWeight: 500, minWidth: 80 }}
          onClick={handleSubmit}
        >
          Apply
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
    >
      <div className="relative inline-flex max-w-full">
        <Button
          aria-label="Select submitted date range"
          title={displayText}
          style={{
            width: 'fit-content',
            maxWidth: '100%',
            minWidth: 0,
            height: 29,
            background: '#fff',
            border: '1px solid #E5E7EB',
            color: dates.startDate && dates.endDate ? '#4649EC' : '#6E6893',
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            paddingLeft: 10,
            paddingRight: dates.startDate && dates.endDate ? 26 : 10,
          }}
        >
          <span
            style={{
              display: 'flex',
              minWidth: 0,
              alignItems: 'center',
              gap: 6,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            <CalendarOutlined style={{ flex: 'none', color: '#4649EC' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {displayText}
            </span>
          </span>
        </Button>
        {dates.startDate && dates.endDate && (
          <button
            type="button"
            aria-label="Clear submitted date range and show all time"
            onClick={handleClear}
            className="absolute right-1 top-1/2 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-[#4649EC] hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-indigo-600"
            style={{
              border: 0,
              background: 'transparent',
            }}
          >
            <CloseOutlined style={{ fontSize: 11 }} />
          </button>
        )}
      </div>
    </Popover>
  );
};

export default DateRangeQuickFilter;
